<template>
  <DialogElement
    ref="dialog"
    :closed-by="'any'"
    :aria-label="t('secondaryMenu.componentTitle')"
    :modal="!deviceHasTextSelectCalloutMenu"
    class="flex secondary-menu absolute justify-center items-center noselect"
    @close="selection?.clear()"
  >
    <nav
      class="flex flex-1 justify-end keep-dialog"
      :aria-label="t('readerTitleBar.navigationBar')"
    >
      <ReadAloudCommand
        v-if="selection?.locator"
        v-slot="btnProps"
        :start-at="selection.locator"
      >
        <SecondaryMenuButton v-bind="btnProps" />
      </ReadAloudCommand>
      <ReadSelectionCommand v-slot="btnProps" :selection="selection">
        <SecondaryMenuButton v-bind="btnProps" />
      </ReadSelectionCommand>
      <TTSSettingsCommand v-slot="btnProps" always-shown>
        <SecondaryMenuButton v-bind="btnProps" icon="graphic_eq" />
      </TTSSettingsCommand>
    </nav>
  </DialogElement>
</template>
<script setup lang="ts">
import { useTemplateRef, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import { computePosition, flip, offset, shift } from "@floating-ui/dom";

import ReadAloudCommand from "@/commands/tts/ReadAloudCommand.vue";
import ReadSelectionCommand from "@/commands/tts/ReadSelectionCommand.vue";
import TTSSettingsCommand from "@/commands/tts/TTSSettingsCommand.vue";

import SecondaryMenuButton from "./SecondaryMenuButton.vue";
import DialogElement from "../shared/DialogElement.vue";
import { Selection } from "../navigator/types";

const props = defineProps<{
  selection: Selection;
}>();

const deviceHasTextSelectCalloutMenu = CSS.supports(
  "-webkit-touch-callout",
  "none"
);

const { t } = useI18n();

const dialog = useTemplateRef<InstanceType<typeof DialogElement>>("dialog");

watchEffect(async () => {
  const el = dialog.value?.$el as HTMLDialogElement;
  if (!el) return;

  // extract boundingRect before async call to keep reactivity
  const boundingRect = props.selection?.position;
  const position = await computePosition(
    {
      getBoundingClientRect: () => boundingRect,
    },
    el,
    {
      placement: "top",
      middleware: [
        offset(25), // space between selection and menu
        flip(),
        shift(),
      ],
    }
  );

  el.style.left = position.x + "px";
  el.style.top = position.y + "px";
});
</script>

<style lang="scss" scoped>
@use "@/assets/styles/reader.scss" as *;
.secondary-menu {
  overflow-y: auto;
  z-index: 3;
  padding: 0;
  margin: 0;
  max-height: var(--safe-height);
  color: var(--DM__theme-font-color);
  background-color: var(--DM__theme-fill-color);
  box-shadow: 0 4px 23px rgb(0 0 0 / 9%);
  border: 3px solid rgba(158, 158, 158, 0.2);
  nav {
    &.keep-dialog {
      padding: $spacingS $spacingM;
    }
  }
}
</style>
